import React from "react";

import CheckIcon from "@mui/icons-material/Check";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import EmailIcon from "@mui/icons-material/Email";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import ShareIcon from "@mui/icons-material/Share";
import { Game } from "../models";
import {
  Box,
  Typography,
  IconButton,
  Link,
  Card,
  Divider,
  Tooltip,
} from "@mui/material";
import i18next from "i18next";
import { useTranslation } from "react-i18next";
import { useTheme as useAppTheme } from "../contexts/ThemeContext";

export function ShareGameLinkControl(props: { game: Game }) {
  const { game } = props;
  const { t } = useTranslation();
  const { themeConfig } = useAppTheme();
  i18next.loadNamespaces("shareGame", (err, t) => {
    /* ... */
  });
  let [copied, setCopied] = React.useState<boolean>(false);
  const copyLink = () => {
    navigator.clipboard.writeText(window.location.href);
    setCopied(true);
    // Reset after 2 seconds
    setTimeout(() => {
      setCopied(false);
    }, 2000);
  };

  return (
    <Card
      elevation={1}
      sx={{
        m: { xs: 2, sm: 3 },
        p: { xs: 3, sm: 4 },
        maxWidth: 700,
        mx: "auto",
        background: themeConfig.dialog.background,
        // border: "4px solid #4A3728",
        // borderRadius: 3,
      }}
    >
      <Box display="flex" flexDirection="column" alignItems="center">
        <ShareIcon sx={{ fontSize: 48, color: "text.secondary", mb: 2 }} />
        <Typography
          variant="h5"
          color="text.secondary"
          fontWeight={600}
          gutterBottom
          textAlign="center"
        >
          {t("shareGame:gameNotStarted")}
        </Typography>
        <Typography
          variant="body2"
          color="text.secondary"
          mb={3}
          textAlign="center"
        >
          {t("shareGame:shareLinkLabel")}
        </Typography>

        {/* Link Box */}
        <Box
          sx={{
            width: "100%",
            p: 2,
            mb: 3,
            bgcolor: "rgba(255, 255, 255, 0.05)",
            borderRadius: 2,
            border: "2px solid",
            borderColor: "divider",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 2,
            flexWrap: "wrap",
          }}
        >
          <Typography
            variant="body2"
            color="text.primary"
            sx={{
              flex: 1,
              wordBreak: "break-all",
              fontSize: { xs: "0.75rem", sm: "0.875rem" },
            }}
          >
            {window.location.href}
          </Typography>
          <Tooltip
            title={copied ? t("shareGame:copied") : t("shareGame:copyLabel")}
          >
            <IconButton
              onClick={copyLink}
              sx={{
                bgcolor: copied ? "primary.main" : "action.hover",
                color: copied ? "primary.contrastText" : "primary.main",
                "&:hover": {
                  bgcolor: copied ? "primary.dark" : "action.selected",
                },
              }}
            >
              {!copied ? <ContentCopyIcon /> : <CheckIcon />}
            </IconButton>
          </Tooltip>
        </Box>

        {/* <Divider sx={{ width: "100%", mb: 3, borderColor: "divider" }} /> */}

        {/* Share Options */}
        <Box
          sx={{
            display: "flex",
            gap: { xs: 2, sm: 3 },
            width: "100%",
            justifyContent: "center",
            flexWrap: "wrap",
          }}
        >
          {/* Copy Button */}
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: 1,
            }}
          >
            <IconButton
              onClick={copyLink}
              sx={{
                width: 64,
                height: 64,
                bgcolor: "action.hover",
                border: "2px solid",
                borderColor: "primary.main",
                color: "primary.main",
                "&:hover": {
                  bgcolor: "action.selected",
                  transform: "translateY(-4px)",
                },
                transition: "all 0.3s",
              }}
            >
              <ContentCopyIcon fontSize="large" />
            </IconButton>
            <Typography
              variant="caption"
              color="text.secondary"
              textAlign="center"
            >
              {t("shareGame:copyLabel")}
            </Typography>
          </Box>

          {/* WhatsApp */}
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: 1,
            }}
          >
            <IconButton
              component={Link}
              target="_blank"
              href={`https://api.whatsapp.com/send?text=Du wurdest zu dem Spiel "${game.title}" eingeladen.\nKlicke auf den folgenden Link um an dem Spiel teilzunehmen: ${window.location.href}`}
              sx={{
                width: 64,
                height: 64,
                bgcolor: "rgba(37, 211, 102, 0.1)",
                border: "2px solid #25D366",
                color: "#25D366",
                "&:hover": {
                  bgcolor: "rgba(37, 211, 102, 0.2)",
                  transform: "translateY(-4px)",
                },
                transition: "all 0.3s",
              }}
            >
              <WhatsAppIcon fontSize="large" />
            </IconButton>
            <Typography
              variant="caption"
              color="text.secondary"
              textAlign="center"
            >
              {t("shareGame:whatsAppLabel")}
            </Typography>
          </Box>

          {/* Email */}
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: 1,
            }}
          >
            <IconButton
              component={Link}
              target="_blank"
              href={`mailto:?&subject=Neues Spiel&body=Du wurdest zu dem Spiel "${game.title}" eingeladen.\nKlicke auf den folgenden Link um an dem Spiel teilzunehmen: ${window.location.href}`}
              sx={{
                width: 64,
                height: 64,
                bgcolor: "rgba(234, 67, 53, 0.1)",
                border: "2px solid #EA4335",
                color: "#EA4335",
                "&:hover": {
                  bgcolor: "rgba(234, 67, 53, 0.2)",
                  transform: "translateY(-4px)",
                },
                transition: "all 0.3s",
              }}
            >
              <EmailIcon fontSize="large" />
            </IconButton>
            <Typography
              variant="caption"
              color="text.secondary"
              textAlign="center"
            >
              {t("shareGame:eMailAppLabel")}
            </Typography>
          </Box>
        </Box>
      </Box>
    </Card>
  );
}
